import RestService from '@core/services/specials/_private/RestService';
import IResponse from '@core/utils/types/IResponse';
import ISpecialEventDto from '@core/mappers/specials/dtos/ISpecialEventDto';
import SpecialEventMapper from '@core/mappers/specials/SpecialEventMapper';
import SpecialEvent, { SpecialEventIncomplete } from '@core/models/specials/SpecialEvent';
import { RequestError } from '@core/errors/network/RequestError';
import ErrorType from '@core/errors/network/ErrorType';
import * as NetworkUtils from '@utils/network/NetworkUtils';
import ServerConfig from '@core/models/country/ServerConfig';
import { instanceGuard } from '@core/utils/services';
import SingletonError from '../common/errors/SingletonError';
import Singleton from '../common/Singleton';
import IIncompleteSpecialEventDto from '@core/mappers/specials/dtos/IIncompleteSpecialEventDto';

class SpecialsService {

    private static instance: SpecialsService;

    public static getInstance(): SpecialsService {
        return instanceGuard(SpecialsService.instance);
    }

    public static createInstance(config: ServerConfig) {
        if (!SpecialsService.instance) {
            SpecialsService.instance = new SpecialsService(config);
        }
        return SpecialsService.instance;
    }

    private constructor(
        private config: ServerConfig,
        private restService = new RestService(config),
    ) {
        if (SpecialsService.instance) {
            throw new SingletonError(this.constructor.name);
        }
    }

    private specialEventMapper = new SpecialEventMapper();

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    // ============================
    // Rest methods
    // ============================

    public async getSpecialsResults(startDate: Date, endDate: Date): Promise<SpecialEventIncomplete[]> {
        const response: IResponse<IIncompleteSpecialEventDto[]> = await NetworkUtils.processResponse(
            this.restService.getSpecialsResults(startDate, endDate)
        );
        return response.data.map(this.specialEventMapper.createTargetObject);
    }

    public async getEvent(matchId: number): Promise<SpecialEvent> {
        const response: IResponse<ISpecialEventDto[]> = await NetworkUtils.processResponse(
            this.restService.getEvents([matchId])
        );
        if (response.data[0]) {
            return this.specialEventMapper.createTargetObject(response.data[0]) as SpecialEvent;
        }
        throw new RequestError(new Error(`Special event with id ${matchId} not found`), ErrorType.resourceNotFound);
    }

    public async getEventsById(matchIds: number[]): Promise<SpecialEvent[]> {
        const response: IResponse<ISpecialEventDto[]> = await NetworkUtils.processResponse(
            this.restService.getEvents(matchIds)
        );
        if (response.data) {
            return response.data.map(this.specialEventMapper.createTargetObject) as SpecialEvent[];
        }
        throw new RequestError(
            new Error(`One of the ids is not correct, or all of them, ${matchIds}`),
            ErrorType.resourceNotFound,
        );
    }

    public async getSpecialOffer(startDate: Date, endDate: Date): Promise<SpecialEventIncomplete[]> {
        const response = await NetworkUtils.processResponse(
            this.restService.getSpecialOffer(startDate, endDate)
        );
        return response.data.map(this.specialEventMapper.createTargetObject) as SpecialEventIncomplete[];
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete SpecialsService.instance;
    }
}

export default SpecialsService as Singleton<SpecialsService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/specials/SpecialsService.ts